import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Spinner from '../components/Shared/Spinner';

const API_BASE = 'https://magenta-esl-solum.azurewebsites.net/api';

interface AgentCheckin {
  checkinAt: string;
  result: 'updated' | 'identical' | 'error' | 'first';
  csvName: string | null;
  lastSuccessfulUpdate: { at: string; csvName: string | null } | null;
}

interface SolumStatus {
  lastRun: string;
  emailsFound?: number;
  filesProcessed?: number;
  emailSubject?: string;
  emailDate?: string;
  rowCount?: number;
  status: string;
  error: string | null;
  agentCheckin?: AgentCheckin | null;
  lastSuccessfulRun?: { at: string; emailSubject?: string; emailDate?: string } | null;
}

const EslSolum: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<SolumStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const [forcing, setForcing] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/status`);
      const data = await res.json();
      setStatus(data);
    } catch {
      setStatus({ lastRun: '', status: 'unreachable', error: 'API inaccessible' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000); // refresh toutes les 30s
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);
    setUploadError(null);

    try {
      const buffer = await file.arrayBuffer();
      const res = await fetch(`${API_BASE}/transform`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: buffer,
      });
      const data = await res.json();

      if (res.ok) {
        setUploadResult(
          `${data.rowCount} articles transformés → ${data.blobName}. ` +
            `Dépôt forcé dans AIMS au prochain passage du serveur SOLUM (max 15 min).`
        );
        fetchStatus();
      } else {
        setUploadError(data.error || 'Erreur inconnue');
      }
    } catch (err: any) {
      setUploadError(err.message || 'Erreur réseau');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = () => {
    window.open(`${API_BASE}/download`, '_blank');
  };

  // Le serveur SOLUM ignore un CSV identique au précédent. Ce drapeau lui dit
  // de le redéposer quand même dans AIMS — sans quoi un ré-import manuel reste
  // sans effet visible.
  const handleForce = async () => {
    setForcing(true);
    setUploadResult(null);
    setUploadError(null);
    try {
      const res = await fetch(`${API_BASE}/force`, { method: 'POST' });
      if (res.ok) {
        setUploadResult(
          'Ré-envoi armé. Le serveur SOLUM redéposera le CSV dans AIMS à son prochain passage (max 15 min).'
        );
      } else {
        setUploadError('Impossible d’armer le ré-envoi');
      }
    } catch (err: any) {
      setUploadError(err.message || 'Erreur réseau');
    } finally {
      setForcing(false);
    }
  };

  const formatDate = (iso: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('fr-CH', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const statusColor = (s: string) => {
    if (s === 'processed') return 'bg-ms-green text-white';
    if (s === 'no_new_emails') return 'bg-ms-gray-200 text-ms-gray-600';
    if (s === 'error' || s === 'unreachable') return 'bg-ms-red text-white';
    return 'bg-ms-gray-200 text-ms-gray-600';
  };

  const statusLabel = (s: string) => {
    if (s === 'processed') return 'Traité';
    if (s === 'no_new_emails') return 'Aucun nouvel email';
    if (s === 'no_data') return 'Aucune donnée';
    if (s === 'error') return 'Erreur';
    if (s === 'unreachable') return 'API inaccessible';
    return s;
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/')}
          className="text-ms-blue text-sm hover:underline mb-2 inline-block"
        >
          ← Retour au portail
        </button>
        <h2 className="text-2xl font-semibold text-ms-gray-800">
          Étiquettes numériques SOLUM
        </h2>
        <p className="text-ms-gray-500 mt-1">
          Transformation automatique des exports Odoo → étiquettes ESL
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne gauche : Statut + Actions */}
        <div className="lg:col-span-2 space-y-6">
          {/* Statut */}
          <div className="bg-white border border-ms-gray-200 rounded-lg p-6">
            <h3 className="text-base font-semibold text-ms-gray-800 mb-4">Statut du service</h3>
            {loading ? (
              <Spinner />
            ) : status ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-1 rounded text-xs font-medium ${statusColor(status.status)}`}>
                    {statusLabel(status.status)}
                  </span>
                  <span className="text-sm text-ms-gray-500">
                    Dernière vérification : {formatDate(status.lastRun)}
                  </span>
                </div>

                {status.status === 'processed' && (
                  <div className="bg-ms-gray-50 rounded p-3 text-sm space-y-1">
                    {status.emailSubject && (
                      <p><span className="text-ms-gray-500">Email :</span> {status.emailSubject}</p>
                    )}
                    {status.emailDate && (
                      <p><span className="text-ms-gray-500">Reçu le :</span> {formatDate(status.emailDate)}</p>
                    )}
                    {status.emailsFound !== undefined && (
                      <p><span className="text-ms-gray-500">Emails trouvés :</span> {status.emailsFound}</p>
                    )}
                    {status.filesProcessed !== undefined && (
                      <p><span className="text-ms-gray-500">Fichiers traités :</span> {status.filesProcessed}</p>
                    )}
                    {status.rowCount !== undefined && (
                      <p><span className="text-ms-gray-500">Articles :</span> {status.rowCount}</p>
                    )}
                  </div>
                )}

                {status.agentCheckin ? (
                  <div className="bg-ms-gray-50 rounded p-3 text-sm space-y-1 border-t border-ms-gray-200 mt-2 pt-3">
                    <p className="font-medium text-ms-gray-700">Serveur SOLUM (10.1.30.25)</p>
                    <p>
                      <span className="text-ms-gray-500">Dernier fetch : </span>
                      {formatDate(status.agentCheckin.checkinAt)}
                    </p>
                    <p>
                      <span className="text-ms-gray-500">Dernier résultat : </span>
                      {status.agentCheckin.result === 'updated' && (
                        <span className="text-green-700 font-medium">CSV mis à jour</span>
                      )}
                      {status.agentCheckin.result === 'identical' && (
                        <span className="text-ms-gray-500">Identique (aucun changement)</span>
                      )}
                      {status.agentCheckin.result === 'first' && (
                        <span className="text-ms-blue font-medium">Premier fetch</span>
                      )}
                      {status.agentCheckin.result === 'error' && (
                        <span className="text-red-600 font-medium">Erreur de téléchargement</span>
                      )}
                    </p>
                    {status.agentCheckin.lastSuccessfulUpdate ? (
                      <p>
                        <span className="text-ms-gray-500">Dernière mise à jour réussie : </span>
                        <span className="text-green-700 font-medium">{formatDate(status.agentCheckin.lastSuccessfulUpdate.at)}</span>
                        {status.agentCheckin.lastSuccessfulUpdate.csvName && (
                          <span className="text-ms-gray-400 text-xs ml-1">— {status.agentCheckin.lastSuccessfulUpdate.csvName}</span>
                        )}
                      </p>
                    ) : (
                      <p className="text-ms-gray-400 text-xs">Aucune mise à jour réussie enregistrée</p>
                    )}
                  </div>
                ) : (
                  <div className="bg-ms-gray-50 rounded p-3 text-sm mt-2 space-y-1">
                    {status.lastSuccessfulRun && (
                      <p>
                        <span className="text-ms-gray-500">Dernière mise à jour CSV : </span>
                        <span className="text-green-700 font-medium">{formatDate(status.lastSuccessfulRun.at)}</span>
                        {status.lastSuccessfulRun.emailSubject && (
                          <span className="text-ms-gray-400 text-xs ml-1">— {status.lastSuccessfulRun.emailSubject}</span>
                        )}
                      </p>
                    )}
                    <p className="text-ms-gray-400">Serveur SOLUM — aucun rapport reçu</p>
                  </div>
                )}

                {status.error && (
                  <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
                    {status.error}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Actions */}
          <div className="bg-white border border-ms-gray-200 rounded-lg p-6">
            <h3 className="text-base font-semibold text-ms-gray-800 mb-4">Actions</h3>
            <div className="flex flex-wrap gap-3">
              {/* Upload manuel */}
              <label className="flex items-center gap-2 px-4 py-2 bg-ms-blue text-white rounded-md hover:bg-ms-blue-dark transition-colors text-sm font-medium cursor-pointer">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                {uploading ? 'Transformation en cours...' : 'Upload XLSX manuel'}
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={handleUpload}
                  disabled={uploading}
                  className="hidden"
                />
              </label>

              {/* Télécharger CSV */}
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-ms-gray-300 text-ms-gray-700 rounded-md hover:bg-ms-gray-50 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Télécharger le dernier CSV
              </button>

              {/* Forcer le ré-envoi vers AIMS */}
              <button
                onClick={handleForce}
                disabled={forcing}
                title="Redépose le CSV actuel dans AIMS même s'il n'a pas changé"
                className="flex items-center gap-2 px-4 py-2 bg-white border border-ms-gray-300 text-ms-gray-700 rounded-md hover:bg-ms-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {forcing ? 'Armement...' : 'Forcer le ré-envoi vers AIMS'}
              </button>

              {/* Rafraîchir */}
              <button
                onClick={fetchStatus}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-ms-gray-300 text-ms-gray-700 rounded-md hover:bg-ms-gray-50 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Rafraîchir
              </button>
            </div>

            {/* Résultat upload */}
            {uploadResult && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded p-3 text-sm text-green-700">
                {uploadResult}
              </div>
            )}
            {uploadError && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
                Erreur : {uploadError}
              </div>
            )}
          </div>

          {/* Fonctionnement */}
          <div className="bg-white border border-ms-gray-200 rounded-lg p-6">
            <h3 className="text-base font-semibold text-ms-gray-800 mb-3">Fonctionnement automatique</h3>
            <div className="flex items-start gap-3 text-sm text-ms-gray-600">
              <div className="flex-shrink-0 w-8 h-8 bg-ms-blue/10 rounded-full flex items-center justify-center text-ms-blue font-semibold">1</div>
              <p className="pt-1">Odoo envoie automatiquement un export XLSX <strong>toutes les heures</strong> (à H-5 min) par email à <strong>solum@magenta.ch</strong></p>
            </div>
            <div className="flex items-start gap-3 text-sm text-ms-gray-600 mt-3">
              <div className="flex-shrink-0 w-8 h-8 bg-ms-blue/10 rounded-full flex items-center justify-center text-ms-blue font-semibold">2</div>
              <p className="pt-1">Azure Function vérifie la boîte toutes les <strong>15 minutes</strong>, transforme le XLSX en CSV SOLUM</p>
            </div>
            <div className="flex items-start gap-3 text-sm text-ms-gray-600 mt-3">
              <div className="flex-shrink-0 w-8 h-8 bg-ms-blue/10 rounded-full flex items-center justify-center text-ms-blue font-semibold">3</div>
              <p className="pt-1">Le serveur SOLUM (10.1.30.25) récupère le CSV toutes les <strong>15 minutes</strong> et met à jour les étiquettes</p>
            </div>
          </div>
        </div>

        {/* Colonne droite : Guide manuel */}
        <div>
          <div className="bg-white border border-ms-gray-200 rounded-lg p-6">
            <button
              onClick={() => setShowGuide(!showGuide)}
              className="flex items-center justify-between w-full text-left"
            >
              <h3 className="text-base font-semibold text-ms-gray-800">
                Guide de secours
              </h3>
              <svg className={`w-5 h-5 text-ms-gray-400 transition-transform ${showGuide ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <p className="text-sm text-ms-gray-500 mt-1">
              Si le flux automatique ne fonctionne plus
            </p>

            {showGuide && (
              <div className="mt-4 space-y-4 text-sm text-ms-gray-600">
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-800">
                  <strong>Procédure manuelle complète</strong> — à suivre si l'automatisation est en panne.
                </div>

                <div>
                  <p className="font-semibold text-ms-gray-700 mb-1">Étape 1 : Export Odoo</p>
                  <ol className="list-decimal list-inside space-y-1 pl-1">
                    <li>Se connecter à Odoo (<strong>magenta.ch</strong>)</li>
                    <li>Activer le <strong>mode debug</strong></li>
                    <li>Aller dans <strong>Paramètres → Technique → Actions planifiées</strong></li>
                    <li>Chercher et exécuter l'action <strong>"Envoi automatique export Excel des produits"</strong></li>
                    <li>Récupérer le fichier dans le chatter du contact <strong>"Régina Jackson"</strong></li>
                  </ol>
                </div>

                <div>
                  <p className="font-semibold text-ms-gray-700 mb-1">Étape 2 : Transformation</p>
                  <p><strong>Option A</strong> — Via cette page :</p>
                  <ol className="list-decimal list-inside space-y-1 pl-1">
                    <li>Cliquer sur <strong>"Upload XLSX manuel"</strong> ci-dessus</li>
                    <li>Sélectionner le fichier exporté</li>
                    <li>Cliquer sur <strong>"Télécharger le dernier CSV"</strong></li>
                  </ol>
                  <p className="mt-2"><strong>Option B</strong> — Si l'API Azure est aussi en panne :</p>
                  <ol className="list-decimal list-inside space-y-1 pl-1">
                    <li>Ouvrir le XLSX dans Excel</li>
                    <li>Ajouter une colonne LIST_PRICE = <code>ARRONDI(list_price×1.081;0)</code></li>
                    <li>Formater les prix avec 2 décimales</li>
                    <li>Réordonner les colonnes : STORE_CODE, ARTICLE_ID, BARCODE, ITEM_NAME, SALE_PRICE, LIST_PRICE, MEUBLE_EXPO, REFERENCE_INTERNE</li>
                    <li>Enregistrer en <strong>CSV UTF-8 (séparateur point-virgule)</strong></li>
                  </ol>
                </div>

                <div>
                  <p className="font-semibold text-ms-gray-700 mb-1">Étape 3 : Import SOLUM</p>
                  <ol className="list-decimal list-inside space-y-1 pl-1">
                    <li>Se connecter au serveur SOLUM via <strong>TeamViewer</strong></li>
                    <li>Copier le CSV dans <code>C:\Program Files\AIMS\inf\input\</code></li>
                    <li>Le serveur SOLUM importe automatiquement les fichiers de ce dossier</li>
                  </ol>
                </div>

                <div className="bg-ms-gray-50 rounded p-3">
                  <p className="font-semibold text-ms-gray-700 mb-1">Contacts</p>
                  <ul className="space-y-1">
                    <li><strong>API Azure</strong> : magenta-esl-solum.azurewebsites.net</li>
                    <li><strong>Serveur SOLUM</strong> : 10.1.30.25</li>
                    <li><strong>Mailbox</strong> : solum@magenta.ch</li>
                    <li><strong>Odoo sender</strong> : mail@magenta.ch</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EslSolum;
